import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle } from "lucide-react"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { normalizarDecimal } from "@/lib/decimales"
import { FECHA_MINIMA, fechaFueraDeRango, hoyLocal } from "@/lib/fechas"

import { comoTasa } from "./formato"
import type { TasaDelPanel } from "./tipos"

const ESTILO = {
  borderColor: "var(--border)",
  backgroundColor: "var(--surface)",
  color: "var(--fg)",
}

/** Hasta seis decimales —los del ledger—, con coma o punto, sin separar los miles. */
const VALOR = /^\d+(\.\d{1,6})?$/

/** Apartarse más que esto de la vigente pide confirmarlo, como en el 2.0. */
const DESVIO_MAXIMO = 15

/**
 * Registrar una tasa a mano.
 *
 * ⚠ **Se teclea en bolívares por 1 unidad, como se lee en la tarjeta.** El 2.0 pedía
 * «cuántos EUR equivalen a 1 USD» y enseñaba bolívares al lado: dos convenciones en la
 * misma pantalla, y un euro tecleado como 968 guardado como 968 euros por dólar. Aquí
 * lo convierte el servidor con el dólar BCV de esa fecha (`panel_tasas.registrar_manual`);
 * sin dólar ese día, responde 409 y se dice por qué.
 *
 * ⚠ **Un valor que se aparta más de un 15 % de la vigente pide confirmación**, no se
 * rechaza: una devaluación de verdad existe. Lo que caza es el cero de más o de menos,
 * que es el error típico al teclear una tasa y el que llega entero a una liquidación.
 *
 * ⚠ **La fecha no puede ser futura.** La vigente es la de fecha más reciente: una tasa
 * fechada mañana regiría hoy para todo el sistema.
 *
 * ⚠ **Registrar dos veces la misma fecha ESTÁ permitido**: es el mecanismo de
 * corrección de la regla 20. La segunda gana y la primera queda como constancia.
 */
export function DialogoTasa({
  tasas,
  inicial,
  onCerrar,
}: {
  tasas: readonly TasaDelPanel[]
  /** La divisa elegida en la pantalla: se abre con ella. */
  inicial: string
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const hoy = hoyLocal()
  const [codigo, setCodigo] = useState(inicial)
  const [valor, setValor] = useState("")
  const [fecha, setFecha] = useState(hoy)
  const [revisada, setRevisada] = useState(false)

  const tasa = tasas.find((t) => t.codigo === codigo)
  const moneda = tasa?.moneda ?? codigo
  const normalizado = normalizarDecimal(valor) ?? ""
  const fechaValida = fecha !== "" && !fechaFueraDeRango(fecha) && fecha <= hoy
  const valido = VALOR.test(normalizado) && Number(normalizado) > 0 && fechaValida

  const vigente = tasa?.actual?.bs_por_unidad ?? null
  const desvio =
    valido && vigente !== null && Number(vigente) > 0
      ? ((Number(normalizado) - Number(vigente)) / Number(vigente)) * 100
      : null
  const raro = desvio !== null && Math.abs(desvio) > DESVIO_MAXIMO

  const registrar = useMutation({
    mutationFn: () =>
      api.post("/api/v1/tasas", {
        codigo,
        // Como texto: el servidor lo lee como `Decimal` y no pasa por coma flotante.
        tasa: normalizado,
        // Lo tecleado se marca MANUAL: quien mira una tasa necesita saber si la
        // publicó el banco o la escribió alguien.
        fuente: "MANUAL",
        fecha_valor: fecha,
        expresada_en: "bs_por_unidad",
      }),
    onSuccess: () => {
      for (const clave of ["tasas-panel", "tasas-historial", "tasas-vigentes"]) {
        clienteQuery.invalidateQueries({ queryKey: [clave] })
      }
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (valido && (!raro || revisada)) registrar.mutate()
  }

  return (
    <Modal
      abierto={true}
      titulo="Registrar una tasa"
      descripcion="Para cuando el BCV no publica o hay que asentar la que de verdad se usó."
      onCerrar={onCerrar}
      ancho="sm"
    >
      <form onSubmit={enviar} className="grid gap-4">
        <div>
          <label htmlFor="t-divisa" className="mb-1 block text-xs font-medium uppercase">
            Divisa
          </label>
          <select
            id="t-divisa"
            value={codigo}
            onChange={(e) => {
              setCodigo(e.target.value)
              setRevisada(false)
            }}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={ESTILO}
          >
            {tasas.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.moneda} · {t.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="t-valor" className="mb-1 block text-xs font-medium uppercase">
            Bolívares por 1 {moneda}
          </label>
          <input
            id="t-valor"
            inputMode="decimal"
            value={valor}
            onChange={(e) => {
              setValor(e.target.value)
              setRevisada(false)
            }}
            placeholder="832,4883"
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={ESTILO}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            {tasa?.actual?.bs_por_unidad
              ? `Vigente: ${comoTasa(tasa.actual.bs_por_unidad)} Bs (${tasa.actual.fuente}). `
              : ""}
            {moneda === codigo
              ? `Se guarda como ${moneda} por 1 USD, con el dólar BCV de esa fecha.`
              : "Es la tasa del dólar: se guarda tal cual."}
          </p>
        </div>

        <div>
          <label htmlFor="t-fecha" className="mb-1 block text-xs font-medium uppercase">
            Fecha de valor
          </label>
          <input
            id="t-fecha"
            type="date"
            min={FECHA_MINIMA}
            max={hoy}
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={ESTILO}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            El día al que aplica, no el de hoy: una tasa del viernes se registra el lunes y sigue
            siendo la del viernes. Nunca una fecha futura.
          </p>
        </div>

        {raro && desvio !== null && (
          <div
            className="grid gap-2 rounded-[var(--radius-control)] border p-3 text-sm"
            style={{ borderColor: "var(--warning)" }}
          >
            <p className="flex items-start gap-2">
              <AlertTriangle
                size={16}
                aria-hidden={true}
                className="mt-0.5 shrink-0"
                style={{ color: "var(--warning)" }}
              />
              <span>
                Se aparta un {comoTasa(Math.abs(desvio).toFixed(1), 1)} % de la vigente. Revisa que
                no sobre ni falte un cero.
              </span>
            </p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={revisada}
                onChange={(e) => setRevisada(e.target.checked)}
              />
              La he revisado: es correcta
            </label>
          </div>
        )}

        {registrar.isError && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {registrar.error instanceof ErrorApi ? registrar.error.message : "No se pudo registrar"}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            type="submit"
            disabled={!valido || (raro && !revisada) || registrar.isPending}
          >
            {registrar.isPending ? "Registrando…" : "Registrar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
